/* eslint @typescript-eslint/no-var-requires: "off" */
'use client';

const { Swiper } = require('swiper/react');
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { Carousel, CarouselRenderItemInfo } from '@/components/atoms/carousel';
import { IconButton } from '@/components/atoms/iconButton';
import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import RichTextFieldSB, {
  RichTextFieldSBProps,
} from '@/components/atoms/richTextField/RichTextFieldSB';
import { BlockComponentModel } from '@/components/cms/types/blok-component';

import { BlockWrapper } from '@/features/utils/BlockWrapper';
import { gradientBorderStyling } from '@/styles/gradient';

import { CardItem } from './CardDetails';
import { swiperConfig } from './constants';
import { ImageCarousel } from './imageCarousel';

import Arrow from '~/svg/IconButtonChevronRight.svg';

export type ImageCarouselStrapiProps = {
  image: ImageSBProps;
};

export type ImageWithCardsCarouselProps = {
  badgeText?: string;
  cardTitle?: string;
  cardDescription: RichTextFieldSBProps;
  image: ImageSBProps;
};

export type ImageWithCardsCarouselStrapiProps = {
  carouselContent: ImageWithCardsCarouselProps[];
};

export function ImageWithCardsCarouselStrapi({
  block,
}: BlockComponentModel<ImageWithCardsCarouselStrapiProps>) {
  const { carouselContent } = block;

  const [swiperRef, setSwiperRef] = useState<typeof Swiper | null>(null);
  const [activeCard, setActiveCard] = useState(1);
  const [isCarouselClicked, setIsCarouselClicked] = useState(false);
  const isMultipleItems = carouselContent?.length > 1;

  const images = carouselContent.map(({ image }, index) => {
    return { image, id: index + 1 };
  });

  const cards = carouselContent.map(
    ({ badgeText, cardDescription, cardTitle }, index) => {
      return {
        badgeText,
        cardDescription: (
          <RichTextFieldSB
            block={cardDescription}
            classNames={{ link: { root: 'underline text-neutral-N99' } }}
          />
        ),
        cardTitle,
        id: index + 1,
      };
    }
  );

  const renderCarouselImage = ({
    item,
  }: CarouselRenderItemInfo<ImageCarouselStrapiProps>) => (
    <ImageCarousel
      {...item}
      image={
        <ImageSB
          block={item.image}
          inner={{
            classNames: {
              image: 'rounded-xl !shadow-image m-4 border border-neutral-N04',
            },
          }}
        />
      }
      renderImage={(props) => <ImageSB inner={props} block={item.image} />}
    />
  );

  const handleRightArrowClick = () => {
    swiperRef!.slideNext();
    setActiveCard((prevState) =>
      prevState < cards.length ? prevState + 1 : 1
    );
    setIsCarouselClicked(true);
  };

  const handleLeftArrowClick = () => {
    swiperRef!.slidePrev();
    setActiveCard((prevState) =>
      prevState === 1 ? cards.length : prevState - 1
    );
    setIsCarouselClicked(true);
  };

  return (
    <BlockWrapper
      theme='light'
      classNames={{
        root: 'hidden m:block m:!py-6 lg:!px-0 lg:w-[80%] lg:mx-auto',
      }}
    >
      <div
        data-tracker-section='image-with-cards-carousel'
        className='relative w-full max-w-[1024px]	'
      >
        {isMultipleItems && (
          <IconButton
            onClick={handleLeftArrowClick}
            className='z-5 hover:bg-neutral-N04 absolute left-[-40px] top-1/2 flex h-10 w-10 -translate-y-1/2 rotate-180 transform cursor-pointer rounded-full p-2 lg:left-[-10%] lg:!h-12 lg:!w-12'
            data-tracker='top-banner-left-arrow'
            classNames={{ inner: 'items-center' }}
          >
            <Arrow />
          </IconButton>
        )}
        <div className=''>
          <Carousel
            items={images}
            config={{
              ...swiperConfig,
              loop: isMultipleItems ? true : false,
              onSwiper: (swiper: typeof Swiper) => setSwiperRef(swiper),
              onAutoplay: () =>
                setActiveCard((prevState) =>
                  prevState < images.length ? prevState + 1 : 1
                ),
            }}
            renderItem={renderCarouselImage}
            sideGradient={false}
          />
          {cards.map(
            (item, index) =>
              item.id === activeCard && (
                <div className='' key={index}>
                  <div
                    className={cn(
                      `bg-neutral-N00/60 parallax-effect border-gradient absolute right-[40px] top-[65px] z-10 transform overflow-hidden rounded-3xl backdrop-blur-[12px] transition-opacity duration-500 ease-in-out lg:right-[0%] ${gradientBorderStyling(
                        {
                          borderWidth: 'p-[1px]',
                          borderRadius: 'rounded-3xl',
                        }
                      )}`
                    )}
                  >
                    <CardItem
                      key={item.id}
                      {...item}
                      items={cards}
                      activeCard={activeCard}
                      isCarouselClicked={isCarouselClicked}
                      setActiveCard={setActiveCard}
                      setIsCarouselClicked={setIsCarouselClicked}
                      swiper={swiperRef}
                    />
                  </div>
                </div>
              )
          )}
        </div>
        {isMultipleItems && (
          <IconButton
            onClick={handleRightArrowClick}
            className='z-5 hover:bg-neutral-N04 absolute right-[-40px] top-1/2 flex -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full p-2 lg:right-[-10%] lg:!h-12 lg:!w-12'
            data-tracker='top-banner-right-arrow'
          >
            <Arrow />
          </IconButton>
        )}
      </div>
    </BlockWrapper>
  );
}
